import { useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import { useStore } from "../../stores/store"
import { conversationStore, openConversation, closeConversation, sendMessage, visibleMessages } from "../../stores/conversationStore"
import { useTranslations } from "../../lib/translations"

// Kinds the user actually cares about in the chat. Everything else
// (span.model_request_start, agent.thinking, tool_use, etc.) is
// Anthropic SSE telemetry — surfaced via a typing indicator instead.
const VISIBLE_KINDS = new Set(["user", "agent"])

export function ChatTab({ agent }) {
  const t = useTranslations()
  const entry = useStore(conversationStore, (s) => s.byAgent[agent.id])
  const [text, setText] = useState("")
  const scroller = useRef(null)

  useEffect(() => {
    openConversation(agent.id).catch(() => {})
    return () => closeConversation(agent.id)
  }, [agent.id])

  const allMessages = entry?.messages || []
  const messages = visibleMessages(allMessages).filter((m) => VISIBLE_KINDS.has(m.kind))
  const sending = entry?.sending

  // Agent is "thinking" when the most recent user message has no
  // agent reply after it (or we're mid-send).
  const lastUserIdx = lastIndexWhere(messages, (m) => m.kind === "user")
  const lastAgentIdx = lastIndexWhere(messages, (m) => m.kind === "agent")
  const thinking = sending || (lastUserIdx > -1 && lastUserIdx > lastAgentIdx)

  // auto-scroll to bottom on new messages or typing indicator state change
  useEffect(() => {
    if (!scroller.current) return
    scroller.current.scrollTop = scroller.current.scrollHeight
  }, [messages.length, thinking])

  const submit = async (e) => {
    e?.preventDefault?.()
    if (!text.trim() || sending) return
    const t2 = text
    setText("")
    try { await sendMessage(agent.id, t2) }
    catch (err) { setText(t2); alert(err.message) }
  }

  const onKey = (e) => {
    // Cmd+Enter (mac) or Ctrl+Enter submits. Plain Enter inserts a newline.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <>
      <div className="cm-tabwrap" ref={scroller}>
        {messages.length === 0 && !thinking && (
          <div className="cm-coming-soon">No messages yet — send the first one below.</div>
        )}
        {messages.map((m) => <Message key={m.id} m={m} agent={agent} />)}
        {thinking && <TypingIndicator />}
      </div>
      <form className="cm-composer" onSubmit={submit}>
        <textarea value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={t("modal.composer.placeholder", { name: agent.name })}
                  disabled={sending}
                  rows={1} />
        <button type="submit" disabled={sending || !text.trim()}>
          {t("modal.composer.send")}
        </button>
      </form>
    </>
  )
}

function lastIndexWhere(arr, pred) {
  for (let i = arr.length - 1; i >= 0; i--) if (pred(arr[i])) return i
  return -1
}

function Message({ m, agent }) {
  const isUser = m.kind === "user"
  return (
    <div className={isUser ? "cm-msg me" : "cm-msg"}>
      <div className="av">{isUser ? "U" : "A"}</div>
      <div className="bub">
        {isUser
          ? <span>{m.content}</span>
          : <ReactMarkdown>{m.content || ""}</ReactMarkdown>}
      </div>
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="cm-msg cm-typing">
      <div className="av">A</div>
      <div className="bub">
        <span className="cm-typing-dot" />
        <span className="cm-typing-dot" />
        <span className="cm-typing-dot" />
      </div>
    </div>
  )
}
